import React, { useState, useMemo } from "react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import {
  ChevronLeft,
  Building,
  MapPin,
  Folder,
  User,
  Phone,
  Mail,
  Calendar,
  FileText,
  Shield,
  StickyNote,
  RefreshCw,
  Users,
  UserCheck,
  Clock,
  MessageSquare,
  Building2,
  Menu,
  X,
  Search,
  Download,
} from "lucide-react";

import {
  useGetAllVendorsQuery,
  useGetVisitLogsByVendorQuery,
} from "../../slices/vendorApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const VendorManagement = () => {
  const [currentView, setCurrentView] = useState("vendorList");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [exportAcademyId, setExportAcademyId] = useState("all");

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const academyId = userByEmial?.user?.academyId;

  // Get all academies for filtering
  const { data: academies = [] } = useGetAcademyQuery();

  // Get academy name by ID
  const getAcademyName = (academyId) => {
    if (!academyId || academyId === "all" || !academies.length) return "N/A";
    const academy = academies.find(
      (acad) => acad.academyId === academyId || acad._id === academyId,
    );
    return academy?.name || "N/A";
  };

  // Prepare academy options for CustomSelect
  const academyOptions = useMemo(() => {
    return academies.map((academy) => ({
      value: academy.academyId || academy._id,
      label: academy.name,
      academyId: academy.academyId || academy._id,
      name: academy.name,
    }));
  }, [academies]);

  // Fetch all vendors without academy filter
  const { data: vendors = [], isLoading: vendorsLoading } =
    useGetAllVendorsQuery();

  // Helper to extract academy from vendor
  const getVendorAcademy = (vendor) => {
    if (!vendor) return { id: null, name: null };

    // Case 1: academy is a populated object with name
    if (vendor.academy && typeof vendor.academy === "object") {
      return {
        id:
          vendor.academy._id ||
          vendor.academy.id ||
          vendor.academy.academyId ||
          null,
        name: vendor.academy.name || vendor.academy.academyName || null,
      };
    }

    // Case 2: academyId exists, find the academy name from academies list
    if (vendor.academyId) {
      const foundAcademy = academies?.find(
        (acad) =>
          acad._id === vendor.academyId ||
          acad.id === vendor.academyId ||
          acad.academyId === vendor.academyId,
      );
      return {
        id: vendor.academyId,
        name: foundAcademy?.name || foundAcademy?.academyName || null,
      };
    }

    // Case 3: academyName exists directly on vendor
    if (vendor.academyName) {
      return {
        id: vendor.academyId || null,
        name: vendor.academyName,
      };
    }

    return { id: null, name: null };
  };

  // Export Vendor List to Excel
  const exportVendorListToExcel = () => {
    try {
      if (!vendors || vendors.length === 0) {
        alert("There are no vendors to export.");
        return;
      }

      // Filter vendors based on export academy selection
      const vendorsToExport = vendors.filter((vendor) => {
        if (exportAcademyId === "all" || exportAcademyId === null) {
          return true; // All academies
        }
        const vendorAcademy = getVendorAcademy(vendor);
        const idMatch =
          vendorAcademy.id &&
          exportAcademyId &&
          vendorAcademy.id === exportAcademyId;
        return idMatch;
      });

      if (vendorsToExport.length === 0) {
        return;
      }

      // Create data array with title and vendor data
      const exportData = [];

      // Add title based on academy selection
      if (exportAcademyId === "all" || exportAcademyId === null) {
        // All Academies - include academy column
        exportData.push(["LIST OF REGISTERED VENDORS - ALL ACADEMIES"]);
        exportData.push([]); // Empty row
        exportData.push(["Report Date:", new Date().toLocaleDateString()]);
        exportData.push(["Total Vendors:", vendorsToExport.length]);
        exportData.push([
          "Total Academies:",
          new Set(vendorsToExport.map((v) => getVendorAcademy(v).name)).size,
        ]);
        exportData.push([]); // Empty row
        exportData.push([]); // Empty row

        // Add table headers with academy column
        exportData.push([
          "Vendor Name",
          "Address",
          "Vendor ID",
          "Contact Person",
          "Phone Number",
          "Email",
          "Academy",
          "Contract Start",
          "Contract End",
          "Visit Frequency",
          "BG Serial Number",
          "BG Expiry",
          "Note",
          "Status", // Added Status column
        ]);

        // Add vendor data with academy
        vendorsToExport.forEach((vendor) => {
          const vendorAcademy = getVendorAcademy(vendor);
          const vendorStatus = getVendorStatus(vendor);

          exportData.push([
            vendor.name || "-",
            vendor.vendorIdentity || "-",
            vendor.address || "-",
            vendor.contactPersonName || vendor.contactPerson || "-",
            vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
            vendor.contactPersonEmail || vendor.email || "-",
            vendorAcademy.name || "No Academy",
            formatDate(vendor.contractStartDate || vendor.contractStart),
            formatDate(vendor.contractEndDate || vendor.contractEnd),
            (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
              " per year",
            vendor.bgSerialNumber || "-",
            formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
            vendor.note || "-",
            vendorStatus, // Added status
          ]);
        });
      } else {
        // Single Academy - academy name in title, no academy column
        const academyName = getAcademyName(exportAcademyId);
        exportData.push([
          `LIST OF REGISTERED VENDORS - ${academyName.toUpperCase()}`,
        ]);
        exportData.push([]); // Empty row
        exportData.push(["Academy:", academyName]);
        exportData.push(["Report Date:", new Date().toLocaleDateString()]);
        exportData.push(["Total Vendors:", vendorsToExport.length]);
        exportData.push([]); // Empty row
        exportData.push([]); // Empty row

        // Add table headers without academy column
        exportData.push([
          "Vendor Name",
          "Vendor ID",
          "Address",
          "Contact Person",
          "Phone Number",
          "Email",
          "Contract Start",
          "Contract End",
          "Visit Frequency",
          "BG Serial Number",
          "BG Expiry",
          "Note",
          "Status", // Added Status column
        ]);

        // Add vendor data without academy
        vendorsToExport.forEach((vendor) => {
          const vendorStatus = getVendorStatus(vendor);

          exportData.push([
            vendor.name || "-",
            vendor.vendorIdentity || "-",
            vendor.address || "-",
            vendor.contactPersonName || vendor.contactPerson || "-",
            vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
            vendor.contactPersonEmail || vendor.email || "-",
            formatDate(vendor.contractStartDate || vendor.contractStart),
            formatDate(vendor.contractEndDate || vendor.contractEnd),
            (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
              " per year",
            vendor.bgSerialNumber || "-",
            formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
            vendor.note || "-",
            vendorStatus, // Added status
          ]);
        });
      }

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths based on whether we have academy column or not
      if (exportAcademyId === "all" || exportAcademyId === null) {
        // All Academies - with academy column
        ws["!cols"] = [
          { wch: 25 },
          { wch: 40 },
          { wch: 20 },
          { wch: 15 },
          { wch: 25 },
          { wch: 20 },
          { wch: 12 },
          { wch: 12 },
          { wch: 8 },
          { wch: 20 },
          { wch: 12 },
          { wch: 30 },
          { wch: 8 },
          { wch: 10 }, // Status column width
        ];
      } else {
        // Single Academy - without academy column
        ws["!cols"] = [
          { wch: 25 },
          { wch: 40 },
          { wch: 20 },
          { wch: 15 },
          { wch: 25 },
          { wch: 12 },
          { wch: 12 },
          { wch: 8 },
          { wch: 20 },
          { wch: 12 },
          { wch: 30 },
          { wch: 8 },
          { wch: 10 }, // Status column width
        ];
      }

      // Add worksheet to workbook
      const sheetName =
        exportAcademyId === "all" || exportAcademyId === null
          ? "All Academies"
          : getAcademyName(exportAcademyId).substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate Excel file and download
      const fileName =
        exportAcademyId === "all" || exportAcademyId === null
          ? `all_academies_vendors_${
              new Date().toISOString().split("T")[0]
            }.xlsx`
          : `${getAcademyName(exportAcademyId).replace(
              /[^a-zA-Z0-9]/g,
              "_",
            )}_vendors_${new Date().toISOString().split("T")[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting vendor list:", error);
      alert("Failed to export vendor list. Please try again.");
    }
  };

  // Export function for visit logs - Single sheet with vendor and academy details at top
  const exportVendorLogsToExcel = (vendor, visitLogs) => {
    try {
      if (!vendor || !visitLogs || visitLogs.length === 0) {
        alert("No visit logs available to export.");
        return;
      }

      const vendorAcademy = getVendorAcademy(vendor);

      // Create data array for single sheet
      const exportData = [];

      // Add vendor and academy information at the top
      exportData.push([`VENDOR VISIT LOGS REPORT `]);
      exportData.push([]);
      exportData.push(["Academy Information:"]);
      exportData.push(["Academy Name:", vendorAcademy.name || "No Academy"]);
      exportData.push([]);
      exportData.push(["Vendor Information:"]);
      exportData.push(["Vendor Name:", vendor.name || "-"]);
      exportData.push(["Vendor ID:", vendor.vendorIdentity || "-"]);
      exportData.push(["Address:", vendor.address || "-"]);
      exportData.push([
        "Contact Person:",
        vendor.contactPersonName || vendor.contactPerson || "-",
      ]);
      exportData.push([
        "Phone Number:",
        vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
      ]);
      exportData.push([
        "Email:",
        vendor.contactPersonEmail || vendor.email || "-",
      ]);
      exportData.push([
        "Contract Start:",
        formatDate(vendor.contractStartDate || vendor.contractStart),
      ]);
      exportData.push([
        "Contract End:",
        formatDate(vendor.contractEndDate || vendor.contractEnd),
      ]);
      exportData.push([
        "Visit Frequency:",
        (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
          " per year",
      ]);
      exportData.push(["BG Serial Number:", vendor.bgSerialNumber || "-"]);
      exportData.push([
        "BG Expiry:",
        formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
      ]);
      exportData.push(["Note:", vendor.note || "-"]);
      exportData.push([]);
      exportData.push(["Report Summary:"]);
      exportData.push(["Total Visit Logs:", visitLogs.length]);
      exportData.push(["Export Date:", new Date().toLocaleDateString()]);
      exportData.push([]);
      exportData.push([]); // Empty row for separation

      // Add visit logs header
      exportData.push([
        "Visit Date",
        "Start Time",
        "End Time",
        "Purpose",
        "Description",
        "Supervisor Comment",
        "Logged By",
        "Logged Date",
      ]);

      // Add visit logs data
      visitLogs.forEach((log) => {
        exportData.push([
          formatDate(log.visitDate),
          formatTime(log.startTime),
          formatTime(log.endTime),
          log.purpose || "-",
          log.log || log.description || "-",
          log.comment || log.supervisorComment || "-",
          log.loggedByName || log.loggedBy || "-",
          formatDate(log.createdAt),
        ]);
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 25 },
        { wch: 40 },
        { wch: 40 },
        { wch: 20 },
        { wch: 12 },
      ];

      // Add worksheet to workbook
      const sheetName = `${vendor.name.substring(0, 20)} Visit Logs`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate Excel file and download
      const fileName = `${vendor.name.replace(/[^a-zA-Z0-9]/g, "_")}_${
        vendorAcademy.name?.replace(/[^a-zA-Z0-9]/g, "_") || "no_academy"
      }_visit_logs_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting vendor logs:", error);
      alert("Failed to export visit logs. Please try again.");
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const timeWithoutSeconds = timeString.split(":").slice(0, 2).join(":");
      const [hours, minutes] = timeWithoutSeconds.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  // Get vendor status based on contract end date
  const getVendorStatus = (vendor) => {
    const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
    if (!contractEndDate) return "Unknown";

    const endDate = new Date(contractEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate >= today ? "Active" : "Expired";
  };

  // Navigation Functions
  const handleBackToVendorList = () => {
    setCurrentView("vendorList");
    setSelectedVendor(null);
  };

  const handleViewVendorDetails = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentView("vendorDetails");
  };

  const handleViewLogDetails = (log) => {
    setSelectedLog(log);
    setCurrentView("logPreview");
  };

  const renderCurrentView = () => {
    if (vendorsLoading) {
      return (
        <div className="min-h-screen bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading vendors...</p>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case "vendorList":
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            academies={academies}
            academyOptions={academyOptions}
            selectedAcademyId={selectedAcademyId}
            setSelectedAcademyId={setSelectedAcademyId}
            getAcademyName={getAcademyName}
            getVendorAcademy={getVendorAcademy}
            getVendorStatus={getVendorStatus}
            exportAcademyId={exportAcademyId}
            setExportAcademyId={setExportAcademyId}
            onExportVendorList={exportVendorListToExcel}
          />
        );
      case "vendorDetails":
        return (
          <VendorDetails
            vendor={selectedVendor}
            onBack={handleBackToVendorList}
            onViewLog={handleViewLogDetails}
            getVendorAcademy={getVendorAcademy}
            getVendorStatus={getVendorStatus}
            onExportVendorLogs={exportVendorLogsToExcel}
          />
        );
      case "logPreview":
        return (
          <LogPreviewPage
            log={selectedLog}
            vendor={selectedVendor}
            onBack={() => setCurrentView("vendorDetails")}
          />
        );
      default:
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            academies={academies}
            academyOptions={academyOptions}
            selectedAcademyId={selectedAcademyId}
            setSelectedAcademyId={setSelectedAcademyId}
            getAcademyName={getAcademyName}
            getVendorAcademy={getVendorAcademy}
            getVendorStatus={getVendorStatus}
            exportAcademyId={exportAcademyId}
            setExportAcademyId={setExportAcademyId}
            onExportVendorList={exportVendorListToExcel}
          />
        );
    }
  };

  return (
    <div className="h-full bg-white p-4 sm:py-6 sm:px-8 space-y-4">
      {renderCurrentView()}
    </div>
  );
};

const VendorList = ({
  vendors,
  onViewVendor,
  academies,
  academyOptions,
  selectedAcademyId,
  setSelectedAcademyId,
  getAcademyName,
  getVendorAcademy,
  getVendorStatus,
  exportAcademyId,
  setExportAcademyId,
  onExportVendorList,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Get vendor academy name for display
  const getVendorAcademyName = (vendor) => {
    const vendorAcademy = getVendorAcademy(vendor);
    return vendorAcademy.name || "No Academy";
  };

  // Filter vendors based on search term and selected academy
  const filteredVendors = vendors
    .filter((vendor) => {
      const matchesSearch =
        vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false;

      // Academy filter
      let matchesAcademy = true;
      if (selectedAcademyId && selectedAcademyId !== "all") {
        const vendorAcademy = getVendorAcademy(vendor);
        const idMatch =
          vendorAcademy.id &&
          selectedAcademyId &&
          vendorAcademy.id === selectedAcademyId;
        matchesAcademy = idMatch;
      }

      return matchesSearch && matchesAcademy;
    })
    .sort((a, b) => {
      // Sort by createdAt in descending order (newest first)
      // Try createdAt first, then updatedAt, then fallback to 0
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA; // Latest first
    });

  // Calculate active vendors based on contract end date
  const calculateActiveVendors = () => {
    return filteredVendors.filter((vendor) => {
      const status = getVendorStatus(vendor);
      return status === "Active";
    }).length;
  };

  // Calculate statistics
  const totalVendors = filteredVendors.length;
  const activeVendors = calculateActiveVendors();

  // Get status color - Shadcn Badge Variant Style
  const getStatusVariant = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Expired":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // VisitCount component that fetches logs for each vendor
  const VisitCount = ({ vendorId }) => {
    const { data: visitLogs, isLoading } = useGetVisitLogsByVendorQuery(
      vendorId,
      {
        skip: !vendorId,
      },
    );

    if (isLoading) {
      return <Badge variant="outline">Loading...</Badge>;
    }

    const visitCount = Array.isArray(visitLogs) ? visitLogs.length : 0;
    return (
      <span className="text-sm text-muted-foreground">
        {visitCount} {visitCount === 1 ? "visit" : "visits"}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Statistics Cards */}
      <div className="border-b bg-card px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Users size={20} className="text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{totalVendors}</p>
                <h3 className="text-sm text-muted-foreground">Total Vendors</h3>
              </div>
            </div>
          </div>

          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Clock size={20} className="text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{activeVendors}</p>
                <h3 className="text-sm text-muted-foreground">
                  Active Vendors
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header with Search and Filters */}
      <div className="border-b bg-card px-6 py-4">
        <h3 className="text-lg font-semibold mb-4">Vendor Management</h3>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 md:max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <CustomSelect
            options={academyOptions}
            value={selectedAcademyId}
            onChange={setSelectedAcademyId}
            placeholder="Academy"
            allOptionLabel="All Academies"
            getDisplayValue={getAcademyName}
            allValue="all"
            minWidth={180}
          />
          <CustomSelect
            options={academyOptions}
            value={exportAcademyId}
            onChange={setExportAcademyId}
            placeholder="Export Academy"
            allOptionLabel="All Academies"
            getDisplayValue={getAcademyName}
            allValue="all"
            minWidth={180}
          />
          <Button
            onClick={onExportVendorList}
            disabled={vendors.length === 0}
            className="whitespace-nowrap"
          >
            <Download className="h-4 w-4 mr-2" />
            {vendors.length === 0 ? "No Data to Export" : "Export"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Vendor</TableHead>
                <TableHead className="font-semibold">Vendor ID</TableHead>
                <TableHead className="font-semibold">Address</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Academy</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Visits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {searchTerm || selectedAcademyId !== "all"
                      ? "No vendors found matching your criteria."
                      : "No vendors found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => {
                  const vendorAcademyName = getVendorAcademyName(vendor);
                  const status = getVendorStatus(vendor);

                  return (
                    <TableRow
                      key={vendor.id}
                      className="cursor-pointer"
                      onClick={() => onViewVendor(vendor)}
                    >
                      <TableCell className="font-medium">
                        {vendor.name}
                      </TableCell>
                      <TableCell>{vendor.vendorIdentity}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {vendor.address}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="text-sm">
                            {vendor.contactPersonPhoneNumber ||
                              vendor.phoneNumber}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {vendor.contactPersonEmail || vendor.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{vendorAcademyName}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(status)}>
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <VisitCount vendorId={vendor.id} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// VendorDetails Component - Made Responsive
const VendorDetails = ({
  vendor,
  onBack,
  onViewLog,
  getVendorAcademy,
  getVendorStatus,
  onExportVendorLogs,
}) => {
  const [sortOrder, setSortOrder] = useState("latest");

  // Fetch visit logs for this vendor
  const {
    data: visitLogsData,
    isLoading: logsLoading,
    error: logsError,
  } = useGetVisitLogsByVendorQuery(vendor?.id, {
    skip: !vendor?.id,
    refetchOnMountOrArgChange: true,
  });

  // Extract logs from response
  const visitLogs = visitLogsData?.visitLogs || visitLogsData || [];

  // Handle export visit logs
  const handleExportVisitLogs = () => {
    if (!visitLogs || visitLogs.length === 0) {
      alert("No visit logs available to export.");
      return;
    }
    onExportVendorLogs(vendor, visitLogs);
  };

  // Sort the logs based on selected order
  const sortedLogs = [...visitLogs].sort((a, b) => {
    const dateA = new Date(a.visitDate + "T" + a.startTime);
    const dateB = new Date(b.visitDate + "T" + b.startTime);
    return sortOrder === "latest" ? dateB - dateA : dateA - dateB;
  });

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  // Get vendor academy name
  const getVendorAcademyName = () => {
    const vendorAcademy = getVendorAcademy(vendor);
    return vendorAcademy.name || "No Academy";
  };

  // Get vendor status
  const vendorStatus = getVendorStatus(vendor);

  // Enhanced data getter
  const getVendorField = (fieldName) => {
    const fieldMap = {
      contactPerson: vendor?.contactPersonName || vendor?.contactPerson,
      phoneNumber: vendor?.contactPersonPhoneNumber || vendor?.phoneNumber,
      email: vendor?.contactPersonEmail || vendor?.email,
      contractStart: vendor?.contractStartDate || vendor?.contractStart,
      contractEnd: vendor?.contractEndDate || vendor?.contractEnd,
      bgSerialNumber: vendor?.bgSerialNumber,
      bgExpiry: vendor?.bgExpiryDate || vendor?.bgExpiry,
      visitFrequency: vendor?.visitFrequencyPerYear || vendor?.visitFrequency,
      name: vendor?.name,
      address: vendor?.address,
      note: vendor?.note,
      vendorIdentity: vendor?.vendorIdentity,
    };
    return fieldMap[fieldName] || "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const timeWithoutSeconds = timeString.split(":").slice(0, 2).join(":");
      const [hours, minutes] = timeWithoutSeconds.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  if (!vendor) {
    return (
      <div>
        <div className="text-center py-8">
          <p>Vendor not found</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-black text-white rounded-lg"
          >
            Back to Vendor List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          aria-label="Back"
          onClick={onBack}
          className="md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center border-black rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700 sm:w-6 sm:h-6"
          />
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
              {getVendorField("name")}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Academy: {getVendorAcademyName()}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left Side - Profile */}
        <div className="space-y-4">
          <div className="bg-white-50 border p-4 sm:p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Profile</h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Company profile
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6 sm:mb-8">
              {[
                {
                  label: "Name",
                  value: getVendorField("name"),
                  icon: Building,
                },
                {
                  label: "Vendor ID",
                  value: getVendorField("vendorIdentity"),
                  icon: FileText,
                },
                {
                  label: "Address",
                  value: getVendorField("address"),
                  icon: MapPin,
                },
                {
                  label: "Academy",
                  value: getVendorAcademyName(),
                  icon: Building2,
                },
                {
                  label: "Status",
                  value: vendorStatus,
                  icon: Shield,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <field.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white bg-[#8E8E93] p-1 rounded flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right text-xs sm:text-sm">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Details Group */}
            <div className="space-y-4 mb-6 sm:mb-8">
              {[
                {
                  label: "Contact Person",
                  value: getVendorField("contactPerson"),
                  icon: User,
                },
                {
                  label: "Phone Number",
                  value: getVendorField("phoneNumber"),
                  icon: Phone,
                },
                { label: "Email", value: getVendorField("email"), icon: Mail },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <field.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white bg-[#3776FB] p-1 rounded flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right text-xs sm:text-sm break-all">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contract & Bank Details Group */}
            <div className="space-y-4 mb-6 sm:mb-8">
              {[
                {
                  label: "Contract Start",
                  value: formatDate(getVendorField("contractStart")),
                  icon: Calendar,
                },
                {
                  label: "Contract End",
                  value: formatDate(getVendorField("contractEnd")),
                  icon: Calendar,
                },
                {
                  label: "BG Serial Number",
                  value: getVendorField("bgSerialNumber"),
                  icon: FileText,
                },
                {
                  label: "BG Expiry",
                  value: formatDate(getVendorField("bgExpiry")),
                  icon: Shield,
                },
                {
                  label: "Visit Frequency",
                  value: getVendorField("visitFrequency")
                    ? `${getVendorField("visitFrequency")} per year`
                    : "-",
                  icon: RefreshCw,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <field.icon
                        className={`w-4 h-4 sm:w-5 sm:h-5 text-white p-1 rounded flex-shrink-0 ${
                          field.label === "BG Serial Number"
                            ? "bg-black"
                            : field.label === "Visit Frequency"
                              ? "bg-[#F9971D]"
                              : "bg-[#F94035]"
                        }`}
                      />
                      <span className="text-xs sm:text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right text-xs sm:text-sm">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Note */}
            {getVendorField("note") && getVendorField("note") !== "-" && (
              <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  <h3 className="font-semibold text-sm sm:text-base">Note</h3>
                </div>
                <p className="text-gray-700 text-sm sm:text-base">
                  {getVendorField("note")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Visit Logs */}
        <div className="mb-6 lg:mb-10">
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3 sm:gap-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Visit Logs</h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Visit logs of the vendor
                </p>
              </div>
              {/* Export Visit Logs Button */}
              <button
                className={`w-full sm:w-auto px-4 py-2 bg-white border border-[#E4E4E4] rounded-lg shadow-sm flex items-center justify-center gap-1 whitespace-nowrap hover:bg-gray-50 transition-colors text-sm sm:text-base ${
                  logsLoading || visitLogs.length === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
                onClick={handleExportVisitLogs}
                disabled={logsLoading || visitLogs.length === 0}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {logsLoading
                  ? "Loading..."
                  : visitLogs.length === 0
                    ? "No Logs to Export"
                    : "Export Visit Logs"}
              </button>
            </div>

            <div className="mb-4">
              <select
                value={sortOrder}
                onChange={handleSortChange}
                className="w-full sm:w-half p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent text-sm sm:text-base"
              >
                <option value="latest">Latest</option>
                <option value="earliest">Earliest</option>
              </select>
            </div>

            <div className="space-y-4 max-h-[400px] sm:max-h-[500px] lg:max-h-[645px] overflow-y-auto pr-2">
              {logsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading visit logs...</p>
                </div>
              ) : logsError ? (
                <div className="text-center py-8 text-red-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium">Error loading visit logs</p>
                  <p className="text-sm mt-1">
                    {logsError?.data?.message ||
                      logsError?.error ||
                      "Unable to load visit logs. Please try again."}
                  </p>
                </div>
              ) : sortedLogs && sortedLogs.length > 0 ? (
                sortedLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onViewLog(log)}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm text-gray-500">
                        {formatDate(log.visitDate)}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {formatTime(log.startTime)} - {formatTime(log.endTime)}
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2 text-sm sm:text-base">
                      {log.purpose}
                    </h3>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2">
                      {log.log || "No description"}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No visit logs found for this vendor.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// LogPreviewPage Component - Made Responsive
const LogPreviewPage = ({ log, vendor, onBack }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div>
      <div className="max-w-4xl mx-auto">
        {/* Log Preview Content */}
        <div className=" rounded-2xl shadow-lg p-6 border">
          <div className="space-y-6">
            {/* Header with user info and action buttons - shifted up */}
            <div className="flex justify-between items-center bg-gray-100 border-b-2 border-gray-200 pb-4 -mt-6 -mx-6 px-6 pt-4 rounded-t-2xl">
              <div className="flex items-center justify-between mb-6">
                {/* Back Button */}
                <button
                  aria-label="Back"
                  onClick={onBack}
                  className="flex w-8 h-8 sm:w-10 sm:h-10 items-center justify-center rounded-full hover:bg-gray-200 transition-colors text-gray-700"
                >
                  <ChevronLeft size={24} className="sm:w-6 sm:h-6" />
                </button>

                {/* "Logged by" Text */}
                <div className="text-center flex-1">
                  <span className="font-medium text-sm sm:text-base">
                    <span className="text-gray-600">Logged by</span>
                    <span className="text-blue-500">
                      {" "}
                      {log.loggedBy || log.loggedByName || "User"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-1 last:border-b-0">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2 sm:gap-4">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold flex-1 break-words min-w-0">
                  {log.purpose}
                </h2>
                <p className="text-gray-600 whitespace-nowrap flex-shrink-0 text-base sm:text-lg">
                  {formatTime(log.startTime)} – {formatTime(log.endTime)}
                </p>
              </div>

              <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg">
                {formatDate(log.visitDate)}
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <p className="text-[#555555] whitespace-pre-wrap text-base sm:text-lg leading-relaxed">
                  {log.log || "No description available"}
                </p>
              </div>

              {log.comment && (
                <div>
                  <h3 className="font-semibold text-[#ABABAB] mb-3 text-xs sm:text-sm">
                    Supervisor Comment:
                  </h3>
                  <p className="text-[#555555] whitespace-pre-wrap text-base sm:text-lg leading-relaxed">
                    {log.comment}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorManagement;
